/**
 * Adds a visible, keyboard-operable pause/play control to every Flickity
 * carousel on the page that has autoPlay enabled (WCAG 2.2.2 - Pause, Stop, Hide).
 * Works generically across any section/snippet that initializes Flickity with
 * autoPlay, without needing per-template markup changes.
 */
(function () {
  var PAUSE_ICON =
    '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M7 5a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H7zm8 0a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-2z" fill="currentColor"/></svg>';
  var PLAY_ICON =
    '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M6.906 4.537A.6.6 0 0 0 6 5.053v13.894a.6.6 0 0 0 .906.516l11.723-6.947a.6.6 0 0 0 0-1.032L6.906 4.537z" fill="currentColor"/></svg>';

  var PAUSE_LABEL = "Pause slideshow";
  var PLAY_LABEL = "Play slideshow";

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var enhanced = new WeakSet();

  function getFlickityInstance(carousel) {
    // Theme-owned sliders are initialized with the Flickity constructor
    // bundled in themeVendor, which has its own instance registry separate
    // from the standalone window.Flickity loaded for other consumers -- the
    // same split assets/show-items-without-model.js already handles.
    if (carousel.flickityInstance) return carousel.flickityInstance;
    if (window.themeVendor && window.themeVendor.Flickity) {
      var themeInstance = window.themeVendor.Flickity.data(carousel);
      if (themeInstance) return themeInstance;
    }
    if (window.Flickity) return window.Flickity.data(carousel);
    return null;
  }

  function enhance(carousel) {
    if (enhanced.has(carousel)) return;

    var flkty = getFlickityInstance(carousel);
    if (!flkty || !flkty.options || !flkty.options.autoPlay) return;

    enhanced.add(carousel);

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'slider-autoplay-toggle';
    button.innerHTML =
      '<span class="slider-autoplay-toggle__icon" data-icon-pause>' + PAUSE_ICON + '</span>' +
      '<span class="slider-autoplay-toggle__icon" data-icon-play hidden>' + PLAY_ICON + '</span>';

    var container = carousel.parentElement || carousel;
    if (container && window.getComputedStyle(container).position === 'static') {
      container.style.position = 'relative';
    }
    (container || carousel).appendChild(button);

    var paused = false;

    function setPaused(next) {
      paused = next;
      button.setAttribute('aria-pressed', paused ? 'true' : 'false');
      button.setAttribute('aria-label', paused ? PLAY_LABEL : PAUSE_LABEL);

      var pauseIcon = button.querySelector('[data-icon-pause]');
      var playIcon = button.querySelector('[data-icon-play]');
      if (pauseIcon) pauseIcon.hidden = paused;
      if (playIcon) playIcon.hidden = !paused;

      if (paused) {
        if (typeof flkty.stopPlayer === 'function') flkty.stopPlayer();
      } else {
        if (typeof flkty.playPlayer === 'function') flkty.playPlayer();
      }
    }

    button.addEventListener('click', function () {
      setPaused(!paused);
    });

    setPaused(reducedMotion);
  }

  function sweep() {
    document.querySelectorAll('.flickity-enabled').forEach(enhance);
  }

  document.addEventListener('DOMContentLoaded', sweep);
  window.addEventListener('load', sweep);

  // Flickity carousels can initialize at any time (quick-add modals, Shopify
  // section reloads, Nosto re-renders), so a fixed startup poll would miss any
  // that appear late. Flickity inserts its viewport markup when it
  // initializes, so a debounced observer catches every new carousel with one
  // cheap sweep per burst of mutations; theme sliders also announce
  // themselves via theme:slider:loaded.
  var sweepTimer = null;
  function scheduleSweep() {
    if (sweepTimer) window.clearTimeout(sweepTimer);
    sweepTimer = window.setTimeout(sweep, 150);
  }

  new MutationObserver(scheduleSweep).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('theme:slider:loaded', scheduleSweep);
  document.addEventListener('shopify:section:load', scheduleSweep);
})();
